/**
 * Time Slicer Utilities
 * Story 4.2: Date field grouping by time periods
 *
 * Provides functions for:
 * - Converting dates to sortable period keys (AC-4.2.2, AC-4.2.3)
 * - Generating locale-aware display labels (AC-4.2.4)
 * - Bi-week period calculations
 * - Date field type detection
 */
import {
  startOfWeek,
  startOfYear,
  format,
  getQuarter,
  differenceInCalendarWeeks,
  addWeeks,
  isValid,
  parseISO,
} from 'date-fns';
import type { TimeSlicerPeriod, WeekStartDay } from '../../../types/types/view';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Special key for null/invalid dates - sorts first */
const NULL_DATE_KEY = '__null';
const NULL_DATE_LABEL = '(No Date)';

// =============================================================================
// PERIOD KEY FUNCTIONS
// =============================================================================

/**
 * Parse a date value to Date object
 * Handles Date objects, ISO strings, and invalid values
 */
function parseDate(dateValue: Date | string | null | undefined): Date | null {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return isValid(dateValue) ? dateValue : null;
  if (typeof dateValue === 'string') {
    const parsed = parseISO(dateValue);
    return isValid(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Get sortable period key for a date value
 * Returns ISO-like formats that sort correctly alphabetically
 *
 * @param dateValue - Date object, ISO string, or null
 * @param period - Time slicer period
 * @param weekStartsOn - 0=Sunday, 1=Monday (for week/biweek)
 * @returns Sortable string key (e.g., "2024-12", "2024-W49")
 */
export function getTimePeriodKey(
  dateValue: Date | string | null | undefined,
  period: TimeSlicerPeriod,
  weekStartsOn: WeekStartDay = 0
): string {
  const date = parseDate(dateValue);
  if (!date) return NULL_DATE_KEY;

  const year = date.getFullYear();
  const weekOpts = { weekStartsOn };

  switch (period) {
    case 'day':
      return format(date, 'yyyy-MM-dd'); // 2024-12-04

    case 'week': {
      const weekStart = startOfWeek(date, weekOpts);
      const yearStart = startOfWeek(new Date(weekStart.getFullYear(), 0, 1), weekOpts);
      const weekNumber = differenceInCalendarWeeks(weekStart, yearStart, weekOpts) + 1;
      return `${format(weekStart, 'yyyy')}-W${String(weekNumber).padStart(2, '0')}`; // 2024-W49
    }

    case 'biweek': {
      const { periodNumber } = getBiweekPeriod(date, weekStartsOn);
      return `${year}-B${String(periodNumber).padStart(2, '0')}`; // 2024-B24
    }

    case 'month':
      return format(date, 'yyyy-MM'); // 2024-12

    case 'quarter': {
      const q = getQuarter(date);
      return `${year}-Q${q}`; // 2024-Q4
    }

    case 'year':
      return String(year); // 2024

    default:
      return format(date, 'yyyy-MM-dd');
  }
}

/**
 * Get display label for a period key
 * Uses Intl.DateTimeFormat for locale-aware formatting (AC-4.2.4)
 *
 * @param periodKey - Key from getTimePeriodKey()
 * @param period - Time slicer period
 * @returns Human-readable label (e.g., "December 2024")
 */
export function getTimePeriodLabel(
  periodKey: string,
  period: TimeSlicerPeriod,
  weekStartsOn: WeekStartDay = 0
): string {
  if (periodKey === NULL_DATE_KEY) return NULL_DATE_LABEL;

  const locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US';

  try {
    switch (period) {
      case 'day': {
        // Key: 2024-12-04
        const date = parseISO(periodKey);
        return new Intl.DateTimeFormat(locale, {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }).format(date);
      }

      case 'week': {
        // Key: 2024-W49 - extract and format week start date
        const [yearStr, weekPart] = periodKey.split('-W');
        const year = parseInt(yearStr, 10);
        const weekNum = parseInt(weekPart, 10);
        // Respect configured week start so labels align with grouping buckets
        const jan1 = new Date(year, 0, 1);
        const weekStart = addWeeks(startOfWeek(jan1, { weekStartsOn }), weekNum - 1);
        return `Week of ${new Intl.DateTimeFormat(locale, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }).format(weekStart)}`;
      }

      case 'biweek': {
        // Key: 2024-B24
        const [yearStr, biweekPart] = periodKey.split('-B');
        return `Bi-week ${parseInt(biweekPart, 10)}, ${yearStr}`;
      }

      case 'month': {
        // Key: 2024-12
        const [yearStr, monthStr] = periodKey.split('-');
        const date = new Date(parseInt(yearStr, 10), parseInt(monthStr, 10) - 1, 1);
        return new Intl.DateTimeFormat(locale, {
          month: 'long',
          year: 'numeric',
        }).format(date);
      }

      case 'quarter': {
        // Key: 2024-Q4
        const [yearStr, qPart] = periodKey.split('-Q');
        return `Q${qPart} ${yearStr}`;
      }

      case 'year':
        return periodKey; // Just the year: "2024"

      default:
        return periodKey;
    }
  } catch {
    return periodKey; // Fallback to key if parsing fails
  }
}

/**
 * Calculate bi-week period from a date
 * Bi-weeks are 2-week periods starting from the first week of the year
 *
 * @param date - Date to calculate bi-week for
 * @param weekStartsOn - 0=Sunday, 1=Monday
 * @returns Period start date and period number (1-27)
 */
export function getBiweekPeriod(
  date: Date,
  weekStartsOn: WeekStartDay = 0
): { periodStart: Date; periodNumber: number } {
  const yearStart = startOfYear(date);
  const firstWeekStart = startOfWeek(yearStart, { weekStartsOn });
  const weeksFromStart = differenceInCalendarWeeks(date, firstWeekStart, { weekStartsOn });
  const biweekNumber = Math.floor(weeksFromStart / 2) + 1;

  // Calculate period start
  const periodStart = addWeeks(firstWeekStart, (biweekNumber - 1) * 2);

  return { periodStart, periodNumber: biweekNumber };
}

/**
 * Check if a column type is a date type
 * Use this in GroupingModal to show time slicer UI
 */
export function isDateColumnType(columnType: string): boolean {
  return ['date', 'datetime'].includes(columnType);
}

/**
 * Export the null date key constant for testing
 */
export { NULL_DATE_KEY, NULL_DATE_LABEL };
